/**
 * Import validation — auto-detect column mappings, validate rows,
 * apply defaults, detect duplicates.
 */

import type {
  AdjacencyNode,
  CreateNodeInput,
  StrategicInitiative,
  AdjacencyAxis,
  Phase,
  Status,
  NodeType,
  DataSource,
  RelatednessRing,
} from '../types/adjacency';
import { PHASES as ALL_PHASES, STATUSES as ALL_STATUSES } from './reference-data';

// ── Types ─────────────────────────────────────────────────────────────

export type NodeField = keyof CreateNodeInput;

export interface ColumnMapping {
  csvColumn: string;
  nodeField: NodeField | null;
}

export interface RowResult {
  index: number;
  input: CreateNodeInput | null;
  errors: string[];
  warnings: string[];
  isDuplicate: boolean;
  duplicateOf?: string;
}

export interface ImportValidationResult {
  rows: RowResult[];
  validCount: number;
  errorCount: number;
  duplicateCount: number;
}

// ── Enum lookups (case-insensitive) ───────────────────────────────────

const STRATEGIC_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];

const AXES: AdjacencyAxis[] = [
  'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products', 'Core',
];

// WQ-339: same lists the editor offers (Production and Blocked were rejected)
const PHASES: Phase[] = [...ALL_PHASES];
const STATUSES: Status[] = [...ALL_STATUSES];
const NODE_TYPES: NodeType[] = ['product', 'capability', 'market', 'candidate'];

function fuzzyMatch<T extends string>(value: string, options: T[]): T | null {
  const lower = value.toLowerCase().trim();
  for (const opt of options) {
    if (opt.toLowerCase() === lower) return opt;
  }
  // Partial match — starts with
  for (const opt of options) {
    if (opt.toLowerCase().startsWith(lower) || lower.startsWith(opt.toLowerCase())) return opt;
  }
  return null;
}

// ── Column mapping auto-detect ────────────────────────────────────────

/** Map of header patterns → node field. First match wins. */
const HEADER_PATTERNS: [RegExp, NodeField][] = [
  [/^name$|^title$/i, 'name'],
  [/^desc/i, 'description'],
  [/^strategic.?init/i, 'strategicInitiative'],
  [/^initiative$/i, 'strategicInitiative'],
  [/^axis$|^adjacency.?axis/i, 'adjacencyAxis'],
  [/^phase$/i, 'phase'],
  [/^status$/i, 'status'],
  [/^priority$/i, 'priority'],
  [/^ring$|^relatedness/i, 'relatednessRing'],
  [/^customer|^shared.?customer/i, 'sharedCustomers'],
  [/^cost|^shared.?cost/i, 'sharedCosts'],
  [/^competitor|^common.?competitor/i, 'commonCompetitors'],
  [/^attractiveness/i, 'attractivenessScore'],
  [/^reinforces/i, 'reinforcesCore'],
  [/^type$|^node.?type/i, 'nodeType'],
  [/^source$/i, 'source'],
];

export function autoDetectMappings(headers: string[]): ColumnMapping[] {
  return headers.map((header) => {
    for (const [pattern, field] of HEADER_PATTERNS) {
      if (pattern.test(header)) {
        return { csvColumn: header, nodeField: field };
      }
    }
    return { csvColumn: header, nodeField: null };
  });
}

/** All importable fields for the column-mapping dropdown. */
export const IMPORTABLE_FIELDS: { value: NodeField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'description', label: 'Description' },
  { value: 'strategicInitiative', label: 'Strategic Initiative' },
  { value: 'adjacencyAxis', label: 'Adjacency Axis' },
  { value: 'phase', label: 'Phase' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'relatednessRing', label: 'Relatedness Ring' },
  { value: 'sharedCustomers', label: 'Shared Customers' },
  { value: 'sharedCosts', label: 'Shared Costs' },
  { value: 'commonCompetitors', label: 'Common Competitors' },
  { value: 'attractivenessScore', label: 'Attractiveness Score' },
  { value: 'reinforcesCore', label: 'Reinforces Core' },
  { value: 'nodeType', label: 'Node Type' },
  { value: 'source', label: 'Source' },
];

// ── Row validation & transform ────────────────────────────────────────

function clampScore(v: number): number {
  return Math.max(1, Math.min(10, Math.round(v)));
}

function makeDefaults(): CreateNodeInput {
  return {
    name: '',
    description: '',
    strategicInitiative: 'Other',
    adjacencyAxis: 'Core',
    phase: null,
    status: null,
    priority: null,
    relatednessRing: 1,
    sharedCustomers: 5,
    sharedCosts: 5,
    commonCompetitors: 5,
    attractivenessScore: 5,
    reinforcesCore: false,
    nodeType: 'product',
    source: 'manual' as DataSource,
    edges: [],
    parentIds: [],
    mapPosition: null,
    spPriorityTag: null,
    spSyncReviewStatus: null,
    spListItemId: null,
    lastSyncedAt: null,
    // FA.1: Financial
    estRnD: null,
    estRevenue: null,
    iRnD: null,
    fRnD: null,
    totalBudgetEstimate: null,
    // FA.1: Market sizing
    tam: null,
    sam: null,
    tamAiSuggested: null,
    samAiSuggested: null,
    // FA.1: Timeline
    startDate: null,
    endDate: null,
    mrdDate: null,
    mrdTargetDate: null,
    atdDate: null,
    atqDate: null,
    atsDate: null,
    ganttStartDate: null,
    ganttEndDate: null,
    // FA.1: Resources
    fteAllocation: null,
    iwtSouth: false,
    iwtSouthFte: null,
    isMvp: false,
    // FA.1: SP informational
    devLane: null,
    devTeam: null,
    productManager: null,
    productLine: null,
    // FA.1: Budget linking
    budgetProjectName: null,
    // WQ-032: Sync snapshot
    spSyncSnapshot: null,
  };
}

function buildNodeInput(
  row: string[],
  mappings: ColumnMapping[],
): { input: CreateNodeInput; errors: string[]; warnings: string[] } {
  const input = makeDefaults();
  const errors: string[] = [];
  const warnings: string[] = [];

  for (let i = 0; i < mappings.length; i++) {
    const field = mappings[i].nodeField;
    if (!field) continue;
    const raw = (row[i] ?? '').trim();
    if (!raw) continue;

    switch (field) {
      case 'name':
        input.name = raw;
        break;
      case 'description':
        input.description = raw;
        break;
      case 'strategicInitiative': {
        const matched = fuzzyMatch(raw, STRATEGIC_INITIATIVES);
        if (matched) input.strategicInitiative = matched;
        else warnings.push(`Unknown initiative "${raw}", defaulting to "Other"`);
        break;
      }
      case 'adjacencyAxis': {
        const matched = fuzzyMatch(raw, AXES);
        if (matched) input.adjacencyAxis = matched;
        else warnings.push(`Unknown axis "${raw}", defaulting to "Core"`);
        break;
      }
      case 'phase': {
        const matched = fuzzyMatch(raw, PHASES);
        if (matched) input.phase = matched;
        else warnings.push(`Unknown phase "${raw}"`);
        break;
      }
      case 'status': {
        const matched = fuzzyMatch(raw, STATUSES);
        if (matched) input.status = matched;
        else warnings.push(`Unknown status "${raw}"`);
        break;
      }
      case 'priority': {
        const num = parseInt(raw, 10);
        if (!isNaN(num)) input.priority = num;
        break;
      }
      case 'relatednessRing': {
        const num = parseInt(raw, 10);
        if (!isNaN(num) && num >= 0 && num <= 4) input.relatednessRing = num as RelatednessRing;
        else warnings.push(`Invalid ring "${raw}", defaulting to 1`);
        break;
      }
      case 'sharedCustomers': {
        const num = parseFloat(raw);
        if (!isNaN(num)) input.sharedCustomers = clampScore(num);
        break;
      }
      case 'sharedCosts': {
        const num = parseFloat(raw);
        if (!isNaN(num)) input.sharedCosts = clampScore(num);
        break;
      }
      case 'commonCompetitors': {
        const num = parseFloat(raw);
        if (!isNaN(num)) input.commonCompetitors = clampScore(num);
        break;
      }
      case 'attractivenessScore': {
        const num = parseFloat(raw);
        if (!isNaN(num)) input.attractivenessScore = clampScore(num);
        break;
      }
      case 'reinforcesCore':
        input.reinforcesCore = /^(true|yes|1|y)$/i.test(raw);
        break;
      case 'nodeType': {
        const matched = fuzzyMatch(raw, NODE_TYPES);
        if (matched) input.nodeType = matched;
        else warnings.push(`Unknown node type "${raw}", defaulting to "product"`);
        break;
      }
      case 'source': {
        const lower = raw.toLowerCase();
        if (['priority-list', 'adjacency-map', 'manual'].includes(lower)) {
          input.source = lower as DataSource;
        }
        break;
      }
    }
  }

  // Auto-compute attractiveness if not explicitly mapped
  const hasAttractivenessMapping = mappings.some(m => m.nodeField === 'attractivenessScore');
  if (!hasAttractivenessMapping) {
    input.attractivenessScore = Math.round(
      (input.sharedCustomers + input.sharedCosts + input.commonCompetitors) / 3,
    );
  }

  if (!input.name) {
    errors.push('Name is required');
  }

  return { input, errors, warnings };
}

export function validateAndTransform(
  rawRows: string[][],
  mappings: ColumnMapping[],
  existingNodes: AdjacencyNode[],
): ImportValidationResult {
  const existingNames = new Set(existingNodes.map((n) => n.name.toLowerCase()));
  const rows: RowResult[] = [];
  let validCount = 0;
  let errorCount = 0;
  let duplicateCount = 0;

  for (let i = 0; i < rawRows.length; i++) {
    const { input, errors, warnings } = buildNodeInput(rawRows[i], mappings);

    const isDuplicate = input.name ? existingNames.has(input.name.toLowerCase()) : false;
    const duplicateOf = isDuplicate
      ? existingNodes.find((n) => n.name.toLowerCase() === input.name.toLowerCase())?.name
      : undefined;

    if (isDuplicate) duplicateCount++;
    if (errors.length > 0) errorCount++;
    else validCount++;

    rows.push({
      index: i,
      input: errors.length > 0 ? null : input,
      errors,
      warnings,
      isDuplicate,
      duplicateOf,
    });
  }

  return { rows, validCount, errorCount, duplicateCount };
}

// ── JSON import validation ────────────────────────────────────────────

export function validateJsonImport(
  data: unknown,
  existingNodes: AdjacencyNode[],
): ImportValidationResult {
  if (!Array.isArray(data)) {
    return {
      rows: [{ index: 0, input: null, errors: ['JSON must be an array'], warnings: [], isDuplicate: false }],
      validCount: 0,
      errorCount: 1,
      duplicateCount: 0,
    };
  }

  const existingNames = new Set(existingNodes.map((n) => n.name.toLowerCase()));
  const rows: RowResult[] = [];
  let validCount = 0;
  let errorCount = 0;
  let duplicateCount = 0;

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (!item || typeof item !== 'object') {
      rows.push({ index: i, input: null, errors: ['Invalid object'], warnings: [], isDuplicate: false });
      errorCount++;
      continue;
    }

    const obj = item as Record<string, unknown>;
    const errors: string[] = [];
    const warnings: string[] = [];
    const defaults = makeDefaults();

    const name = typeof obj.name === 'string' ? obj.name.trim() : (typeof obj.title === 'string' ? obj.title.trim() : '');
    if (!name) {
      errors.push('Name is required');
      rows.push({ index: i, input: null, errors, warnings, isDuplicate: false });
      errorCount++;
      continue;
    }

    const input: CreateNodeInput = {
      ...defaults,
      name,
      description: typeof obj.description === 'string' ? obj.description : '',
    };

    // Strategic initiative
    if (typeof obj.strategicInitiative === 'string') {
      const matched = fuzzyMatch(obj.strategicInitiative, STRATEGIC_INITIATIVES);
      if (matched) input.strategicInitiative = matched;
      else warnings.push(`Unknown initiative "${obj.strategicInitiative}"`);
    }

    // Axis
    if (typeof obj.adjacencyAxis === 'string') {
      const matched = fuzzyMatch(obj.adjacencyAxis, AXES);
      if (matched) input.adjacencyAxis = matched;
      else warnings.push(`Unknown axis "${obj.adjacencyAxis}"`);
    }

    // Phase
    if (typeof obj.phase === 'string') {
      const matched = fuzzyMatch(obj.phase, PHASES);
      if (matched) input.phase = matched;
    }

    // Status
    if (typeof obj.status === 'string') {
      const matched = fuzzyMatch(obj.status, STATUSES);
      if (matched) input.status = matched;
    }

    // Numeric fields
    if (typeof obj.priority === 'number') input.priority = obj.priority;
    if (typeof obj.relatednessRing === 'number' && obj.relatednessRing >= 0 && obj.relatednessRing <= 4) {
      input.relatednessRing = obj.relatednessRing as RelatednessRing;
    }
    if (typeof obj.sharedCustomers === 'number') input.sharedCustomers = clampScore(obj.sharedCustomers);
    if (typeof obj.sharedCosts === 'number') input.sharedCosts = clampScore(obj.sharedCosts);
    if (typeof obj.commonCompetitors === 'number') input.commonCompetitors = clampScore(obj.commonCompetitors);
    if (typeof obj.attractivenessScore === 'number') {
      input.attractivenessScore = clampScore(obj.attractivenessScore);
    } else {
      input.attractivenessScore = Math.round(
        (input.sharedCustomers + input.sharedCosts + input.commonCompetitors) / 3,
      );
    }

    if (typeof obj.reinforcesCore === 'boolean') input.reinforcesCore = obj.reinforcesCore;
    if (typeof obj.nodeType === 'string') {
      const matched = fuzzyMatch(obj.nodeType, NODE_TYPES);
      if (matched) input.nodeType = matched;
    }

    const isDuplicate = existingNames.has(name.toLowerCase());
    const duplicateOf = isDuplicate
      ? existingNodes.find((n) => n.name.toLowerCase() === name.toLowerCase())?.name
      : undefined;

    if (isDuplicate) duplicateCount++;
    if (errors.length > 0) errorCount++;
    else validCount++;

    rows.push({ index: i, input, errors, warnings, isDuplicate, duplicateOf });
  }

  return { rows, validCount, errorCount, duplicateCount };
}
